<script setup lang="ts">
import { ref, computed, onUnmounted, watch } from 'vue'
import GbDialog from '@/components/GbDialog.vue'
import GbButton from '@/components/GbButton.vue'
import AssessmentItemCheckbox from '@/presentlevels/AssessmentItemCheckbox.vue'
import { useCopyText } from '@/util/use_copy_text'
import { useVisibilityStore } from '@/visibility/visibility.store'
import { useContentProfileStore } from '@/contentprofile/contentprofile.store.js'
import { usePresentLevelsStore } from '@/presentlevels/presentlevels.store'
import { getAnalyticsTracker } from '@/tracking/analytics/plugin'
import { EVENT_NAMES } from '@/tracking/analytics/event_names'
import type { AnalyticsTracker } from '@/tracking/analytics/analyticsTracker'
import {
  postAnalyzeAssessment,
  postGenerateAssessmentNarrative,
  postAlignedGoalsForSummary,
  AssessmentValidationException,
  type AssessmentItem,
  type AssessmentAnalysisResult,
  type AssessmentValidationError,
} from '@/presentlevels/api_v2.ts'
import type { AlignedGoals, SelectedNeedsData } from '@/presentlevels/presentlevels_types.ts'
import { nanoid } from 'nanoid'
import DOMPurify from 'dompurify'
import type { ReportTypeMetadata } from '@/presentlevels/api_v2.ts'

type ModalStep = 'upload' | 'analyzing' | 'confirm' | 'review' | 'generating' | 'preview'

const visibilityStore = useVisibilityStore()
const contentProfileStore = useContentProfileStore()
const presentLevelsStore = usePresentLevelsStore()

const props = defineProps({
  visible: { type: Boolean, required: true },
  reportMetadata: { type: Object as () => ReportTypeMetadata, required: true },
  subject: { type: String, required: false },
  summaryUUID: { type: String, required: true },
  subjectLabel: { type: String, required: true },
  gradeLabel: { type: String, required: true },
  gradeUUID: { type: String, required: true },
  gradeOrder: { type: Number, required: true },
})

// Get example image based on report name from store
const getExampleImage = (reportName: string) => {
  const base64Image = presentLevelsStore.reportTypeImages[reportName]
  if (base64Image) {
    return `data:image/png;base64,${base64Image}`
  }
  return '' // fallback to empty string if not found
}

const emit = defineEmits<{
  'update:visible': [value: boolean]
  'statement-inserted': [statement: string, alignedGoals: AlignedGoals | null]
  'aligned-goals-fetched': [summaryUUID: string, goals: AlignedGoals]
}>()

const { copyText } = useCopyText()

// Create analytics tracker with context that updates when props change
const analytics = ref<AnalyticsTracker>(getAnalyticsTracker().getChildContext({
  score_report_series: props.reportMetadata.name,
  score_report_title: props.reportMetadata.upload_modal_title
}))

// Update analytics context when props change
watch(() => props.reportMetadata, () => {
  analytics.value = getAnalyticsTracker().getChildContext({
    score_report_series: props.reportMetadata.name,
    score_report_title: props.reportMetadata.upload_modal_title
  })
}, { immediate: true })

// ── Step ───────────────────────────────────────────────────────────────────────
const step = ref<ModalStep>('upload')

// ── File state ────────────────────────────────────────────────────────────────
const selectedFile = ref<File | null>(null)
const filePreviewUrl = ref('')
const isDragOver = ref(false)
const fileInputRef = ref<HTMLInputElement | null>(null)
const aiAcknowledged = ref(false)

// ── Analysis ──────────────────────────────────────────────────────────────────
const analysisResult = ref<AssessmentAnalysisResult | null>(null)
const validationError = ref<AssessmentValidationError | null>(null)
const analyzeError = ref<string | null>(null)
const showAckError = ref(false)
const showFileSizeHelp = ref(false)

// ── Custom item forms ─────────────────────────────────────────────────────────
const showAddStrengthForm = ref(false)
const newStrengthTitle = ref('')
const newStrengthDescription = ref('')
const newStrengthDataRef = ref('')

const showAddNeedForm = ref(false)
const newNeedTitle = ref('')
const newNeedDescription = ref('')
const newNeedDataRef = ref('')

// ── Educator insights ─────────────────────────────────────────────────────────
const insightResponses = ref<Record<string, string>>({})

// ── Generated statement ───────────────────────────────────────────────────────
const generatedStatement = ref('')
const generateError = ref<string | null>(null)
const previewTextRef = ref<HTMLElement | null>(null)

// ── Aligned goals ─────────────────────────────────────────────────────────────
const fetchedAlignedGoals = ref<AlignedGoals | null>(null)
const alignedGoalsFetchError = ref<string | null>(null)
const isFetchingAlignedGoals = ref(false)
const activeAlignedGoalsTraceId = ref<string | null>(null)
let alignedGoalsFetchPromise: Promise<AlignedGoals> | null = null


// ── Computed ──────────────────────────────────────────────────────────────────
const modalHeader = computed(() => {
  if (step.value === 'confirm') return 'Does this look right?'
  if (step.value === 'review') return props.reportMetadata.upload_modal_title
  if (step.value === 'preview') return 'Statement Preview'
  return props.reportMetadata.upload_modal_title
})

const modalSubtitle = computed(() => {
  if (step.value === 'confirm') return "Confirm you've selected the correct file before analyzing."
  if (step.value === 'review') return 'Review and approve suggested strengths and needs, answer contextual questions, and add manual entries.'
  if (step.value === 'preview' || step.value === 'analyzing' || step.value === 'generating') return ''
  return props.reportMetadata.upload_modal_subtitle.replace('${subject}', props.subjectLabel)
})

const exampleImageSrc = computed(() => getExampleImage(props.reportMetadata.name))

const exampleImageTitle = computed(() => props.reportMetadata.image_header)

const showFooter = computed(() => ['confirm', 'review', 'preview'].includes(step.value))

const canAnalyze = computed(() => aiAcknowledged.value && selectedFile.value !== null)

const hasSelections = computed(() => {
  if (!analysisResult.value) return false
  return analysisResult.value.strengths.some(s => s.selected) || analysisResult.value.needs.some(n => n.selected)
})

// ── File handling ─────────────────────────────────────────────────────────────
const SUPPORTED_TYPES = ['application/pdf', 'image/png', 'image/jpeg', 'image/jpg', 'image/webp']
// Must match the presentlevels backend's limit (Azure OpenAI vision API max)
const MAX_FILE_SIZE = 16 * 1024 * 1024
const maxFileSizeMB = computed(() => MAX_FILE_SIZE / (1024 * 1024))

function readFileAsBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = (e) => {
      const result = e.target?.result as string
      resolve(result.split(',')[1])
    }
    reader.onerror = reject
    reader.readAsDataURL(file)
  })
}

function handleFileSelected(file: File) {
  showFileSizeHelp.value = false

  // Validate file type
  if (!SUPPORTED_TYPES.includes(file.type)) {
    validationError.value = {
      type: 'unsupported_type',
      title: 'Unsupported File Type',
      message: 'Please upload a PDF, PNG, or JPG file.'
    }
    analyzeError.value = null
    aiAcknowledged.value = false
    return
  }
  
  // Validate file size
  if (file.size > MAX_FILE_SIZE) {
    validationError.value = {
      type: 'file_too_large',
      title: 'File Too Large',
      message: `File exceeds ${maxFileSizeMB.value} MB limit. Please upload a smaller file.`
    }
    analyzeError.value = null
    aiAcknowledged.value = false
    return
  }
  
  // File is valid, proceed
  if (filePreviewUrl.value) URL.revokeObjectURL(filePreviewUrl.value)
  selectedFile.value = file
  filePreviewUrl.value = URL.createObjectURL(file)
  aiAcknowledged.value = false
  validationError.value = null
  analyzeError.value = null
  step.value = 'confirm'
  
  analytics.value.sendEvent(EVENT_NAMES.PRESENTLEVELS.SCORE_REPORT.UPLOADED_FILE, {
    score_report_filetype: file.type
  })
}

function handleFileInputChange(e: Event) {
  const file = (e.target as HTMLInputElement).files?.[0]
  if (file) handleFileSelected(file)
}

function handleDrop(e: DragEvent) {
  e.preventDefault()
  isDragOver.value = false
  const file = e.dataTransfer?.files[0]
  if (file) handleFileSelected(file)
}

function handleDragOver(e: DragEvent) {
  e.preventDefault()
  isDragOver.value = true
}

function triggerFileInput() {
  fileInputRef.value?.click()
}

function chooseDifferentFile() {
  const previousFileType = selectedFile.value?.type || ''
  
  selectedFile.value = null
  if (filePreviewUrl.value) URL.revokeObjectURL(filePreviewUrl.value)
  filePreviewUrl.value = ''
  aiAcknowledged.value = false
  step.value = 'upload'
  
  analytics.value.sendEvent(EVENT_NAMES.PRESENTLEVELS.SCORE_REPORT.CHOSE_DIFFERENT_FILE, {
    score_report_filetype: previousFileType
  })
}

function retryAnalysis() {
  validationError.value = null
  analyzeFile()
}

// ── Analyze ───────────────────────────────────────────────────────────────────
async function analyzeFile() {
  if (!selectedFile.value) return
  if (!aiAcknowledged.value) {
    showAckError.value = true
    return
  }
  step.value = 'analyzing'
  analyzeError.value = null
  
  analytics.value.sendEvent(EVENT_NAMES.PRESENTLEVELS.SCORE_REPORT.ANALYZED, {
    score_report_filetype: selectedFile.value.type
  })
  
  try {
    const base64 = await readFileAsBase64(selectedFile.value)
    const result = await postAnalyzeAssessment({
      fileData: base64,
      mediaType: selectedFile.value.type,
      filename: selectedFile.value.name,
      beta: visibilityStore.betaVisibility,
      content_profile: contentProfileStore.profileId,
      report_type: props.reportMetadata.name,
      subject: props.subject
    })
    analysisResult.value = result
    insightResponses.value = {}
    showAddStrengthForm.value = false
    showAddNeedForm.value = false
    step.value = 'review'
  } catch (err) {
    if (err instanceof AssessmentValidationException) {
      validationError.value = err.validationError
    } else {
      validationError.value = {
        type: 'error',
        title: 'Analysis failed',
        message: (err as Error).message || 'An unexpected error occurred. Please try again.',
      }
    }
    step.value = 'upload'
  }
}

// ── Item toggling & custom adds ───────────────────────────────────────────────
function toggleItem(item: AssessmentItem) {
  item.selected = !item.selected
}

function addCustomStrength() {
  if (!newStrengthTitle.value.trim() || !analysisResult.value) return
  analysisResult.value.strengths.push({
    id: `custom_${Date.now()}`,
    title: DOMPurify.sanitize(newStrengthTitle.value.trim(), { ALLOWED_TAGS: [], ALLOWED_ATTR: [] }),
    description: DOMPurify.sanitize(newStrengthDescription.value.trim(), { ALLOWED_TAGS: [], ALLOWED_ATTR: [] }),
    dataRef: DOMPurify.sanitize(newStrengthDataRef.value.trim(), { ALLOWED_TAGS: [], ALLOWED_ATTR: [] }),
    selected: true,
    custom: true,
  })
  newStrengthTitle.value = ''
  newStrengthDescription.value = ''
  newStrengthDataRef.value = ''
  showAddStrengthForm.value = false
}

function cancelAddStrength() {
  showAddStrengthForm.value = false
  newStrengthTitle.value = ''
  newStrengthDescription.value = ''
  newStrengthDataRef.value = ''
}

function addCustomNeed() {
  if (!newNeedTitle.value.trim() || !analysisResult.value) return
  analysisResult.value.needs.push({
    id: `custom_${Date.now()}`,
    title: DOMPurify.sanitize(newNeedTitle.value.trim(), { ALLOWED_TAGS: [], ALLOWED_ATTR: [] }),
    description: DOMPurify.sanitize(newNeedDescription.value.trim(), { ALLOWED_TAGS: [], ALLOWED_ATTR: [] }),
    dataRef: DOMPurify.sanitize(newNeedDataRef.value.trim(), { ALLOWED_TAGS: [], ALLOWED_ATTR: [] }),
    selected: true,
    custom: true,
  })
  newNeedTitle.value = ''
  newNeedDescription.value = ''
  newNeedDataRef.value = ''
  showAddNeedForm.value = false
}

function cancelAddNeed() {
  showAddNeedForm.value = false
  newNeedTitle.value = ''
  newNeedDescription.value = ''
  newNeedDataRef.value = ''
}

// ── Generate ──────────────────────────────────────────────────────────────────
async function generateStatement() {
  if (!analysisResult.value) return
  step.value = 'generating'
  generateError.value = null
  
  try {
    // Filter to only selected items
    const selectedStrengths = analysisResult.value.strengths.filter(item => item.selected)
    const selectedNeeds = analysisResult.value.needs.filter(item => item.selected)
    
    // Build educatorInsights array from questions and responses
    const educatorInsights = analysisResult.value.educatorInsightQuestions.map((question, index) => ({
      question,
      response: DOMPurify.sanitize(insightResponses.value[String(index)] || '', { ALLOWED_TAGS: [], ALLOWED_ATTR: [] })
    })).filter(insight => insight.response.trim() !== '')
    
    // Send analytics event with detailed data
    analytics.value.sendEvent(EVENT_NAMES.PRESENTLEVELS.SCORE_REPORT.GENERATED_STATEMENT, {
      selected_strengths: selectedStrengths.map(s => s.title),
      selected_needs: selectedNeeds.map(n => n.title),
      manual_strengths_added: selectedStrengths.some(s => s.custom),
      manual_needs_added: selectedNeeds.some(n => n.custom),
      educator_insights_answered: educatorInsights.length
    })

    // Fetch aligned goals in background using only selected needs
    if (selectedNeeds.length > 0) {
      fetchAlignedGoalsInBackground(selectedNeeds)
    }
    
    const result = await postGenerateAssessmentNarrative({
      assessmentType: analysisResult.value.assessmentType,
      studentFirstName: analysisResult.value.studentFirstName,
      assessmentDate: analysisResult.value.assessmentDate,
      topLevelMetrics: analysisResult.value.topLevelMetrics,
      selectedStrengths,
      selectedNeeds,
      educatorInsights
    }, visibilityStore.betaVisibility, contentProfileStore.profileId, props.reportMetadata.name, props.subject)
    generatedStatement.value = result.statement
    step.value = 'preview'
    
    
  } catch (err) {
    generateError.value = (err as Error).message || 'Failed to generate statement.'
    step.value = 'review'
  }
}

// ── Preview actions ───────────────────────────────────────────────────────────
async function copyToClipboard(event: MouseEvent) {
  try {
    await copyText(generatedStatement.value, event)
    analytics.value.sendEvent(EVENT_NAMES.PRESENTLEVELS.SCORE_REPORT.COPIED_TEXT, {
      score_report_filetype: selectedFile.value?.type || '',
      copied_text_type: 'score_report_statement_preview',
      from_button: true
    })
  } catch (error) {
    console.error('Failed to copy statement:', error)
  }
}

function handleManualCopy() {
  analytics.value.sendEvent(EVENT_NAMES.PRESENTLEVELS.SCORE_REPORT.COPIED_TEXT, {
    score_report_filetype: selectedFile.value?.type || '',
    copied_text_type: 'score_report_statement_preview',
    from_button: false
  })
}

function fetchAlignedGoalsInBackground(selectedNeeds: AssessmentItem[]) {
  // Transform needs to the format expected by the API
  const needsData: SelectedNeedsData[] = selectedNeeds.map(need => {
    // Concatenate title and description for better aligned goals matching
    const needText = need.description 
      ? `${need.title}: ${need.description}` 
      : need.title
    
    return {
      need_text: needText,
      grade_label: props.gradeLabel,
      grade_uuid: props.gradeUUID,
      grade_order: props.gradeOrder
    }
  })
  
  const traceId = nanoid()
  activeAlignedGoalsTraceId.value = traceId
  isFetchingAlignedGoals.value = true
  
  // Store the promise so we can handle it after modal closes
  alignedGoalsFetchPromise = postAlignedGoalsForSummary(
    props.summaryUUID,
    {
      category_label: analysisResult.value?.assessmentType || 'Reading',
      subject_label: props.subjectLabel,
      grade_label: props.gradeLabel,
      needs: needsData,
      plw_trace: traceId
    },
    visibilityStore.betaVisibility,
    contentProfileStore.profileId
  )
  
  alignedGoalsFetchPromise.then(goals => {
    // Ignore stale responses - only process if this is still the active request
    if (activeAlignedGoalsTraceId.value === traceId) {
      fetchedAlignedGoals.value = goals
      alignedGoalsFetchError.value = null
      isFetchingAlignedGoals.value = false
    }
  }).catch(err => {
    // Ignore stale responses - only process if this is still the active request
    if (activeAlignedGoalsTraceId.value === traceId) {
      console.error('Failed to fetch aligned goals:', err)
      alignedGoalsFetchError.value = (err as Error).message || 'Failed to fetch aligned goals'
      fetchedAlignedGoals.value = null
      isFetchingAlignedGoals.value = false
    }
  })
}

function insertStatement() {
  const result = analysisResult.value
  if (!result) return
  
  // Replace newlines with <p></p> for proper HTML rendering in WikiTextEditor
  const formattedStatement = '<p>' + generatedStatement.value.replace(/\n\n/g, '</p><p>') + '</p><p></p>'
  
  // Emit statement with pre-fetched aligned goals (or null if fetch not complete yet)
  emit('statement-inserted', formattedStatement, fetchedAlignedGoals.value)
  
  // If goals are still being fetched, set loading state and handle them when they arrive
  if (isFetchingAlignedGoals.value && alignedGoalsFetchPromise) {
    // Set loading state in store so aligned goals panel shows loading indicator
    presentLevelsStore.loadingAlignedGoals[props.summaryUUID] = true
    
    const summaryUUID = props.summaryUUID
    alignedGoalsFetchPromise.then(goals => {
      // Emit event to save goals that arrived after insertion
      emit('aligned-goals-fetched', summaryUUID, goals)
    }).catch(() => {
      // Error already logged in fetchAlignedGoalsInBackground
      // Clear loading state on error
      presentLevelsStore.loadingAlignedGoals[summaryUUID] = false
    })
  }
  
  analytics.value.sendEvent(EVENT_NAMES.PRESENTLEVELS.SCORE_REPORT.INSERTED_STATEMENT, {
    score_report_filetype: selectedFile.value?.type || '',
    aligned_goals_fetched: fetchedAlignedGoals.value !== null,
    aligned_goals_still_fetching: isFetchingAlignedGoals.value
  })
  
  closeModal()
}

// ── Close / reset ─────────────────────────────────────────────────────────────
function closeModal() {
  emit('update:visible', false)
  setTimeout(resetState, 300)
}

function handleVisibilityChange(newValue: boolean) {
  emit('update:visible', newValue)
  if (!newValue) {
    setTimeout(resetState, 300)
  }
}

function resetState() {
  step.value = 'upload'
  selectedFile.value = null
  if (filePreviewUrl.value) URL.revokeObjectURL(filePreviewUrl.value)
  filePreviewUrl.value = ''
  aiAcknowledged.value = false
  showAckError.value = false
  analysisResult.value = null
  validationError.value = null
  showFileSizeHelp.value = false
  analyzeError.value = null
  generatedStatement.value = ''
  generateError.value = null
  fetchedAlignedGoals.value = null
  alignedGoalsFetchError.value = null
  isFetchingAlignedGoals.value = false
  activeAlignedGoalsTraceId.value = null
  alignedGoalsFetchPromise = null
  showAddStrengthForm.value = false
  showAddNeedForm.value = false
  newStrengthTitle.value = ''
  newStrengthDescription.value = ''
  newStrengthDataRef.value = ''
  newNeedTitle.value = ''
  newNeedDescription.value = ''
  newNeedDataRef.value = ''
  insightResponses.value = {}
}

onUnmounted(() => {
  if (filePreviewUrl.value) URL.revokeObjectURL(filePreviewUrl.value)
})
</script>

<template>
  <GbDialog
    dialog-id="assessment-upload-modal"
    :visible="visible"
    :show-header-content="true"
    :footer-buttons="showFooter"
    :dismissableMask="false"
    @update:visible="handleVisibilityChange"
  >
    <!-- ── Header ────────────────────────────────────────────────────────── -->
    <template #headerContent>
      <div id="assessment-upload-modal_header">
        <span class="text-xl font-bold leading-7">{{ modalHeader }}</span>
        <p v-if="modalSubtitle" class="text-md text-gray-700 font-normal mt-0.5">{{ modalSubtitle }}</p>
      </div>
    </template>

    <!-- ── Body ──────────────────────────────────────────────────────────── -->
    <template #bodyContent>

      <!-- STEP: UPLOAD ─────────────────────────────────────────────────── -->
      <div v-if="step === 'upload'">

        <!-- Validation / analysis error -->
        <div v-if="validationError" class="mb-4 flex flex-col gap-3.5 border-[1.5px] border-[#ffcdd2] rounded-lg p-4 pb-3.5 bg-[#fff5f5]">
          <div class="flex gap-[11px] items-start">
            <span class="material-icons flex-shrink-0 mt-0.5 text-text-2xl text-[#c62828]">warning</span>
            <div class="flex-1">
              <p class="font-bold text-[13.5px] text-[#c62828] mb-1">{{ validationError.title }}</p>
              <p class="text-[12.5px] text-gray-700 leading-[1.55]">{{ validationError.message }}</p>
            </div>
          </div>

          <div v-if="validationError.type === 'file_too_large'">
            <button
              type="button"
              class="flex items-center gap-1 text-[12.5px] font-semibold text-[#c62828] cursor-pointer"
              :aria-expanded="showFileSizeHelp"
              @click="showFileSizeHelp = !showFileSizeHelp"
            >
              <span
                class="material-icons-round text-base transition-transform"
                :class="showFileSizeHelp ? 'rotate-90' : ''"
              >chevron_right</span>
              How do I shrink this file?
            </button>
            <ul
              v-if="showFileSizeHelp"
              class="list-disc mt-2 pl-[37px] flex flex-col gap-1.5 text-[12.5px] text-gray-700 leading-[1.55]"
            >
              <li><strong>On a Mac:</strong> open the PDF in Preview, choose File &gt; Export, select "Reduce File Size" under Quartz Filter, then Save.</li>
              <li><strong>On Windows:</strong> if you have Acrobat or Word, use their "Compress PDF" or "Reduce file size" option.</li>
              <li><strong>Trim to just the report:</strong> if the scan includes extra pages or other subject areas, removing them first will shrink the file.</li>
            </ul>
          </div>

          <button
            class="w-full font-semibold bg-white text-[#c62828] border-[1.5px] border-[#ef9a9a] rounded px-4 py-[7px] text-[13px] cursor-pointer transition-colors hover:bg-[#ffebee]"
            @click="validationError = null; showFileSizeHelp = false"
          >
            Upload a different file
          </button>
          <button
            v-if="validationError.type !== 'file_too_large'"
            class="w-full font-semibold text-[#c62828] text-[13px] cursor-pointer transition-colors hover:underline"
            @click="retryAnalysis"
          >
            Try analyzing again
          </button>
        </div>

        <template v-if="!validationError">
          <!-- Sample image -->
          <div class="mb-4 flex flex-col items-center">
            <p class="text-lg text-gray-700 mb-1">{{ exampleImageTitle }}</p>
            <div class="flex flex-col items-center max-w-[600px]">
              <img 
                :src="exampleImageSrc" 
                :alt="exampleImageTitle" 
                class="border border-gray-200 w-full max-h-[315px] object-cover object-top"
              />
              <div v-if="props.reportMetadata.name === 'i-Ready'" class="flex justify-around w-full mt-2">
                <p class="text-base text-gray-700 font-medium">Diagnostic Results</p>
                <p class="text-base text-gray-700 font-medium">Diagnostic Growth</p>
              </div>
            </div>
          </div>

          <!-- Drop zone -->
          <div
            class="flex flex-col items-center gap-2 cursor-pointer transition-colors rounded-lg p-4 text-center"
            :class="[
              isDragOver ? 'border-2 border-dashed border-[#1565c0] bg-[#e8f0fe]' : 'border-2 border-dashed border-gray-300 bg-white'
            ]"
            @dragover="handleDragOver"
            @dragleave="isDragOver = false"
            @drop="handleDrop"
            @click="triggerFileInput"
          >
          <span class="material-icons-round block text-2xl" :class="isDragOver ? 'text-[#1565c0]' : 'text-gray-400'">upload</span>
          <p class="text-lg text-gray-700 leading-normal">Drag and drop your file here</p>
          <button
            type="button"
            class="text-white font-semibold bg-[#1565c0] border-none rounded px-5 py-[7px] text-md cursor-pointer transition-colors hover:bg-[#1976d2]"
            autofocus
            @click.stop="triggerFileInput"
          >
            Select file
          </button>
          <p class="text-sm text-gray-700">PDF, PNG, or JPG · max {{ maxFileSizeMB }} MB</p>
          <input
            ref="fileInputRef"
            type="file"
            accept=".pdf,.png,.jpg,.jpeg,.webp"
            class="hidden"
            @change="handleFileInputChange"
          />
        </div>

        <!-- Privacy notice -->
        <div class="flex items-start gap-[9px] bg-[#f0fdf4] border border-[#bbf7d0] rounded-md p-3 mt-5">
          <span class="material-icons-round flex-shrink-0 mt-px text-xl text-[#16a34a]">shield</span>
          <div>
            <p class="text-md font-bold text-[#15803d] mb-0.5">Your file will not be stored permanently.</p>
            <p class="text-sm text-[#166534] leading-normal">Score reports are processed in real time and immediately discarded.</p>
          </div>
        </div>
        </template>
      </div>

      <!-- STEP: ANALYZING ────────────────────────────────────────────── -->
      <div v-else-if="step === 'analyzing'" class="flex flex-col items-center justify-center py-10 text-center">
        <div class="w-10 h-10 border-4 border-gray-200 border-t-blue-500 rounded-full animate-spin mb-4" />
        <p class="text-lg font-bold text-gray-800 mb-1">Analyzing Assessment</p>
        <p class="text-sm text-gray-600 mb-1">Extracting score data from your report...</p>
        <p class="text-xs text-gray-400">This may take 10–20 seconds.</p>
      </div>

      <!-- STEP: CONFIRM ──────────────────────────────────────────────── -->
      <div v-else-if="step === 'confirm' && selectedFile">
        <!-- File preview -->
        <div class="border border-gray-200 rounded-lg overflow-hidden mb-3 bg-gray-50 h-[400px]">
          <iframe
            v-if="selectedFile.type === 'application/pdf'"
            :src="filePreviewUrl"
            class="w-full h-full"
            title="PDF preview"
          />
          <img
            v-else
            :src="filePreviewUrl"
            :alt="selectedFile.name"
            class="w-full h-full object-contain"
          />
        </div>

        <!-- File info -->
        <div class="flex items-center gap-2 text-sm text-gray-600 mb-4">
          <span class="material-icons-outlined text-base text-gray-400">description</span>
          <span class="flex-1 truncate text-gray-700 font-medium">{{ selectedFile.name }}</span>
          <span class="text-gray-700 shrink-0">{{ Math.round(selectedFile.size / 1024) }} KB</span>
        </div>

        <!-- AI acknowledgment -->
        <div class="mb-3">
          <div
            class="transition-all rounded-r-md p-3"
            :class="[
              showAckError ? 'border-l-[3px] border-l-[#c62828] bg-[#fff5f5] outline outline-1 outline-[#ffcdd2]' : 'border-l-[3px] border-l-[#1565c0] bg-[#f0f6ff]'
            ]"
          >
            <p class="font-bold text-md text-gray-900 mb-2">Acknowledgement of Responsible AI Use</p>
            <label class="cursor-pointer">
              <div class="flex items-center gap-[9px]">
                <input
                  type="checkbox"
                  v-model="aiAcknowledged"
                  class="accent-blue-600 flex-shrink-0 cursor-pointer w-[15px] h-[15px]"
                  @change="showAckError = false"
                />
                <span class="text-md font-semibold text-gray-900">I understand that AI is used in this feature</span>
              </div>
              <p class="text-sm text-gray-700 mt-1 leading-normal pl-6">AI is used to analyze uploaded score reports and generate draft present level statements. As the educator, I am ultimately responsible for the accuracy and appropriateness of the final content.</p>
            </label>
          </div>
          <!-- Inline error message shown when user tries to proceed without acknowledging -->
          <p v-if="showAckError" class="flex items-center gap-[5px] text-xs text-[#c62828] mt-[5px]">
            <span class="material-icons text-[13px] text-[#c62828]">warning</span>
            Please acknowledge responsible AI use before proceeding.
          </p>
        </div>
      </div>

      <!-- STEP: REVIEW ───────────────────────────────────────────────── -->
      <div v-else-if="step === 'review' && analysisResult">

        <!-- Strengths ──────────────────────────────────────────────── -->
        <div class="mb-[18px]">
          <h3 class="text-xl font-bold mb-2.5">
            Strengths ({{ analysisResult.strengths.filter(s => s.selected).length }})
          </h3>

          <AssessmentItemCheckbox
            v-for="item in analysisResult.strengths"
            :key="item.id"
            :item="item"
            @toggle="toggleItem"
          />

          <!-- Add Strength form -->
          <div v-if="showAddStrengthForm" class="border border-gray-200 rounded-md p-3 mb-2 bg-gray-50">
            <input
              v-model="newStrengthTitle"
              type="text"
              placeholder="Strength title *"
              class="fs-exclude w-full px-2.5 py-[7px] mb-[7px] border border-gray-300 rounded text-[13px] text-gray-800 outline-none transition-colors focus:border-[#1565c0] focus-visible:ring-2 focus-visible:ring-[#1565c0]"
            />
            <input
              v-model="newStrengthDescription"
              type="text"
              placeholder="Brief description (optional)"
              class="fs-exclude w-full px-2.5 py-[7px] mb-[7px] border border-gray-300 rounded text-[13px] text-gray-800 outline-none transition-colors focus:border-[#1565c0] focus-visible:ring-2 focus-visible:ring-[#1565c0]"
            />
            <input
              v-model="newStrengthDataRef"
              type="text"
              placeholder="Data reference — e.g., 80% accuracy on 3/14/25 (optional)"
              class="fs-exclude w-full px-2.5 py-[7px] mb-2.5 border border-gray-300 rounded text-[13px] text-gray-800 outline-none transition-colors focus:border-[#1565c0] focus-visible:ring-2 focus-visible:ring-[#1565c0]"
            />
            <div class="flex gap-2">
              <button
                class="text-white border-none rounded-md px-3.5 py-[5px] text-[13px] font-semibold transition-colors"
                :class="newStrengthTitle.trim() ? 'bg-[#1565c0] cursor-pointer' : 'bg-gray-300 cursor-not-allowed'"
                :disabled="!newStrengthTitle.trim()"
                @click="addCustomStrength"
              >Add Strength</button>
              <button
                class="bg-white text-gray-700 border border-gray-300 rounded-md px-3.5 py-[5px] text-[13px] cursor-pointer transition-colors hover:bg-gray-100"
                @click="cancelAddStrength"
              >Cancel</button>
            </div>
          </div>
          <button
            v-else
            class="bg-transparent border-none cursor-pointer inline-flex items-center gap-[5px] text-[13.5px] text-[#1565c0] font-semibold mt-0.5"
            @click="showAddStrengthForm = true"
          >
            <span class="text-xl leading-none">+</span> Add Strength
          </button>
        </div>

        <!-- Needs ──────────────────────────────────────────────────── -->
        <div class="mb-[22px]">
          <h3 class="text-lg font-bold mb-2.5">
            Needs ({{ analysisResult.needs.filter(n => n.selected).length }})
          </h3>

          <AssessmentItemCheckbox
            v-for="item in analysisResult.needs"
            :key="item.id"
            :item="item"
            @toggle="toggleItem"
          />

          <!-- Add Need form -->
          <div v-if="showAddNeedForm" class="border border-gray-200 rounded-md p-3 mb-2 bg-gray-50">
            <input
              v-model="newNeedTitle"
              type="text"
              placeholder="Need title *"
              class="fs-exclude w-full px-2.5 py-[7px] mb-[7px] border border-gray-300 rounded text-[13px] text-gray-800 outline-none transition-colors focus:border-[#1565c0] focus-visible:ring-2 focus-visible:ring-[#1565c0]"
            />
            <input
              v-model="newNeedDescription"
              type="text"
              placeholder="Brief description (optional)"
              class="fs-exclude w-full px-2.5 py-[7px] mb-[7px] border border-gray-300 rounded text-[13px] text-gray-800 outline-none transition-colors focus:border-[#1565c0] focus-visible:ring-2 focus-visible:ring-[#1565c0]"
            />
            <input
              v-model="newNeedDataRef"
              type="text"
              placeholder="Data reference — e.g., 80% accuracy on 3/14/25 (optional)"
              class="fs-exclude w-full px-2.5 py-[7px] mb-2.5 border border-gray-300 rounded text-[13px] text-gray-800 outline-none transition-colors focus:border-[#1565c0] focus-visible:ring-2 focus-visible:ring-[#1565c0]"
            />
            <div class="flex gap-2">
              <button
                class="text-white border-none rounded-md px-3.5 py-[5px] text-[13px] font-semibold transition-colors"
                :class="newNeedTitle.trim() ? 'bg-[#1565c0] cursor-pointer' : 'bg-gray-300 cursor-not-allowed'"
                :disabled="!newNeedTitle.trim()"
                @click="addCustomNeed"
              >Add Need</button>
              <button
                class="bg-white text-gray-700 border border-gray-300 rounded-md px-3.5 py-[5px] text-[13px] cursor-pointer transition-colors hover:bg-gray-100"
                @click="cancelAddNeed"
              >Cancel</button>
            </div>
          </div>
          <button
            v-else
            class="bg-transparent border-none cursor-pointer inline-flex items-center gap-[5px] text-[13.5px] text-[#1565c0] font-semibold mt-0.5"
            @click="showAddNeedForm = true"
          >
            <span class="text-xl leading-none">+</span> Add Need
          </button>
        </div>

        <!-- Educator Insights ──────────────────────────────────────── -->
        <div v-if="analysisResult.educatorInsightQuestions.length > 0">
          <h3 class="text-xl font-bold mb-1">Educator Insights</h3>
          <p class="text-md text-gray-600 mb-3.5 leading-normal">
            Provide additional context to help generate a more comprehensive narrative.
          </p>
          <div
            v-for="(question, index) in analysisResult.educatorInsightQuestions"
            :key="index"
            class="mb-3.5"
          >
            <p class="text-md text-gray-800 font-medium mb-[7px] leading-normal">
              {{ index + 1 }}. {{ question }}
            </p>
            <textarea
              v-model="insightResponses[String(index)]"
              placeholder="Your response…"
              rows="3"
              class="fs-exclude w-full p-3 border border-gray-300 rounded-md text-[13.5px] leading-[1.65] text-gray-800 resize-y outline-none transition-colors focus:border-[#1565c0] focus-visible:ring-2 focus-visible:ring-[#1565c0]"
            />
          </div>
        </div>

        <p v-if="generateError" class="text-[13px] text-[#c62828] mt-1">{{ generateError }}</p>
      </div>

      <!-- STEP: GENERATING ───────────────────────────────────────────── -->
      <div v-else-if="step === 'generating'" class="flex flex-col items-center justify-center py-10 text-center">
        <div class="w-10 h-10 border-4 border-gray-200 border-t-blue-500 rounded-full animate-spin mb-4" />
        <p class="text-lg font-bold text-gray-900 mb-1">Generating Statement</p>
        <p class="text-sm text-gray-800 mb-1">Writing your PLAAFP narrative...</p>
        <p class="text-xs text-gray-700">This may take 10–20 seconds.</p>
      </div>

      <!-- STEP: PREVIEW ──────────────────────────────────────────────── -->
      <div v-else-if="step === 'preview'">
        <p 
          ref="previewTextRef"
          class="text-lg text-gray-800 leading-relaxed whitespace-pre-wrap"
          @copy="handleManualCopy"
        >{{ generatedStatement }}</p>
      </div>

    </template>

    <!-- ── Footer ────────────────────────────────────────────────────────── -->
    <template #footerButtonLeft>
      <!-- Preview: Copy to Clipboard pushed left via mr-auto -->
      <button
        v-if="step === 'preview'"
        class="flex items-center gap-1.5 text-sm text-gray-700 hover:text-gray-800 transition-colors mr-auto"
        @click="copyToClipboard"
      >
        <span class="material-icons-outlined text-base">content_copy</span>
        Copy to Clipboard
      </button>
      <!-- Review: Cancel on left -->
      <GbButton
        v-else-if="step === 'review'"
        gbTheme="grayOutlineSecondary"
        label="Cancel"
        @click="closeModal"
      />
    </template>

    <template #footerButtonRight>
      <!-- Confirm step -->
      <template v-if="step === 'confirm'">
        <GbButton
          gbTheme="grayOutlineSecondary"
          label="Choose a different file"
          @click="chooseDifferentFile"
        />
        <GbButton
          gbTheme="bluePrimary"
          label="Looks good, analyze it"
          @click="analyzeFile"
        />
      </template>

      <!-- Review step -->
      <GbButton
        v-else-if="step === 'review'"
        gbTheme="bluePrimary"
        label="Generate Statement"
        :disabled="!hasSelections"
        @click="generateStatement"
      />

      <!-- Preview step -->
      <template v-else-if="step === 'preview'">
        <GbButton
          gbTheme="grayOutlineSecondary"
          label="Cancel"
          @click="closeModal"
        />
        <GbButton
          gbTheme="bluePrimary"
          label="Insert Statement"
          @click="insertStatement"
        />
      </template>
    </template>
  </GbDialog>
</template>
